import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAlert } from "../../context/AlertContext";
import api from "../../utils/api";
import { getError } from "../../utils/getError";
import { useTrackingReplaysStore } from "../../store/admin/trackingReplaysStore";
import { useUserStore } from "../../store/userStore";
import { connectSocket, subscribeToDevices, getLastPosition } from "../../socket";
import { GoogleMap, useJsApiLoader, OverlayView } from "@react-google-maps/api";
import { brand } from "../../config/theme";

interface TrackPoint {
  lat: number;
  long: number;
  event?: Record<string, unknown>;
  date: string;
  heading?: number;
}

interface RecordingConfig {
  vehicleId: string;
  vehicleName: string;
  deviceId: string;
  latitude: number;
  longitude: number;
  speed?: number;
  engineStatus?: boolean;
  battery?: number;
  gps_sat?: number;
  recordingName: string;
  durationHours: number;
  status?: string;
  heading?: number;
}

const SYNC_INTERVAL_MS = 5 * 60 * 1000;
const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";
const DEFAULT_CENTER = { lat: 23.6345, lng: -102.5528 };
const DEFAULT_ZOOM = 15;

// Marker sizes matching MapView
const MARKER_SIZE = 32;
const DRIVING_MARKER_SIZE = 30;
const DRIVING_BORDER_WIDTH = 3;

// Vehicle status colors matching MapView
const VEHICLE_STATUS_COLORS: { [key: string]: string } = {
  driving: brand.primary,
  conduciendo: brand.primary,
  stopped: "#EF4444",
  stop: "#EF4444",
  detenido: "#EF4444",
  detenida: "#EF4444",
  idle: "#F59E0B",
  ralenti: "#F59E0B",
  sleep: "#3B82F6",
  offline: "#6B7280",
  online: brand.primaryHover,
};

function formatPointsForApi(points: TrackPoint[]) {
  return points.map((p) => ({
    lat: p.lat,
    long: p.long,
    date: p.date,
    heading: p.heading,
    event: p.event ?? {},
  }));
}

// Get marker shape based on vehicle status and heading
const getMarkerShape = (status: string, heading?: number | null) => {
  const s = status?.toLowerCase() || "offline";
  // Calculate rotation for driving marker (heading comes in degrees, 0 = north/up)
  const rotation = heading != null ? heading : 0;
  
  if (s === "driving" || s === "conduciendo") {
    return {
      shape: "polygon",
      icon: null,
      clipPath: "polygon(50% 0%, 100% 100%, 50% 75%, 0% 100%)",
      borderRadius: undefined,
      size: DRIVING_MARKER_SIZE,
      hasBorder: true,
      borderWidth: DRIVING_BORDER_WIDTH,
      rotation,
    };
  }
  if (s === "stopped" || s === "stop" || s === "detenido" || s === "detenida") {
    return {
      shape: "square",
      icon: "ri-stop-line",
      borderRadius: "0%",
      clipPath: undefined,
      size: MARKER_SIZE,
      hasBorder: false,
      borderWidth: 0,
      rotation: 0,
    };
  }
  if (s === "idle" || s === "ralenti" || s === "ralentí" || s === "sleep") {
    return {
      shape: "circle",
      icon: "ri-pause-circle-line",
      borderRadius: "50%",
      clipPath: undefined,
      size: MARKER_SIZE,
      hasBorder: false,
      borderWidth: 0,
      rotation: 0,
    };
  }
  // offline / default
  return {
    shape: "circle",
    icon: "ri-truck-line",
    borderRadius: "50%",
    clipPath: undefined,
    size: MARKER_SIZE,
    hasBorder: false,
    borderWidth: 0,
    rotation: 0,
  };
};

// Get status color
const getStatusColor = (status: string) => {
  const s = status?.toLowerCase() || "offline";
  return VEHICLE_STATUS_COLORS[s] || "#6B7280";
};

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
}

export default function TrackingReplayRecordingPage() {
  const { t } = useTranslation("lite");
  const { showAlert } = useAlert();
  const [searchParams] = useSearchParams();
  
  const addTrackingReplayToStore = useTrackingReplaysStore((s) => s.addTrackingReplayToStore);
  const updateTrackingReplayInStore = useTrackingReplaysStore((s) => s.updateTrackingReplayInStore);
  const user = useUserStore((s) => s.user);

  const [config, setConfig] = useState<RecordingConfig | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [trackPoints, setTrackPoints] = useState<TrackPoint[]>([]);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const recordingStartTimeRef = useRef<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [recordingEndDate, setRecordingEndDate] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recordingFinished, setRecordingFinished] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentHeading, setCurrentHeading] = useState<number>(0);

  // Theme classes matching PublicTrackingSidebar
  const themeClasses = {
    bg: "bg-dark-surface",
    border: "border-dark-border",
    text: "text-white",
    textSecondary: "text-gray-400",
    cardBg: "bg-dark-card",
    cardBorder: "border-dark-border",
    buttonHover: "hover:bg-dark-card",
  };

  const replayIdRef = useRef<string | null>(null);
  const pointsRef = useRef<TrackPoint[]>([]);
  const syncIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoStopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentPositionRef = useRef<{ lat: number; lng: number } | null>(null);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const getUserInfo = useCallback(() => {
    if (user) {
      return {
        id: String(user.uid ?? user.id ?? ""),
        company_id: String(user.company_id ?? user.companyId ?? ""),
      };
    }
    
    try {
      const userStorage = localStorage.getItem("user-storage");
      if (userStorage) {
        const parsed = JSON.parse(userStorage);
        if (parsed.state?.user) {
          const userData = parsed.state.user;
          return {
            id: String(userData.uid ?? userData.id ?? ""),
            company_id: String(userData.company_id ?? userData.companyId ?? ""),
          };
        }
      }
    } catch (err) {
      console.error("Error getting user info from storage:", err);
    }
    
    return { id: "", company_id: "" };
  }, [user]);

  // Load config from sessionStorage
  useEffect(() => {
    const configKey = searchParams.get("configKey");
    if (!configKey) {
      setError("No se encontró la configuración de grabación");
      return;
    }

    const storedConfig = sessionStorage.getItem(configKey);
    if (!storedConfig) {
      setError("La configuración de grabación ha expirado. Por favor, inicia una nueva grabación.");
      return;
    }

    try {
      const parsedConfig = JSON.parse(storedConfig) as RecordingConfig;
      setConfig(parsedConfig);
      currentPositionRef.current = { lat: parsedConfig.latitude, lng: parsedConfig.longitude };
    } catch (err) {
      setError("Error al cargar la configuración de grabación");
    }
  }, [searchParams]);

  const clearSyncInterval = useCallback(() => {
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
      syncIntervalRef.current = null;
    }
  }, []);

  const clearAutoStopTimeout = useCallback(() => {
    if (autoStopTimeoutRef.current) {
      clearTimeout(autoStopTimeoutRef.current);
      autoStopTimeoutRef.current = null;
    }
  }, []);

  const pushPositionsToBackend = useCallback(
    async (replayId: string, positions: TrackPoint[]) => {
      const endDate = new Date().toISOString();
      const { data } = await api.put(
        "/trackingReplays",
        {
          end_date: endDate,
          positions: formatPointsForApi(positions),
        },
        { params: { id: replayId } },
      );
      if (data?.data) {
        updateTrackingReplayInStore(replayId, data.data);
      }
    },
    [updateTrackingReplayInStore],
  );

  const stopRecording = useCallback(async () => {
    const id = replayIdRef.current;
    if (!id) return;

    const positions = pointsRef.current;
    
    // Verificar si hay solo un punto
    if (positions.length === 1) {
      showAlert({
        title: "Advertencia",
        text: "La grabación solo tiene un punto registrado. Si detienes la grabación ahora, la grabación será eliminada. ¿Deseas continuar?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar grabación",
        cancelButtonText: "Continuar grabando",
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#6b7280",
        onConfirm: async () => {
          // Eliminar la grabación
          try {
            await api.delete("/trackingReplays", { params: { id } });
            useTrackingReplaysStore.getState().removeTrackingReplayFromStore(id);
            
            clearSyncInterval();
            clearAutoStopTimeout();
            setIsRecording(false);
            setRecordingFinished(true);
            
            showAlert({
              title: "Grabación eliminada",
              text: "La grabación con un solo punto ha sido eliminada correctamente.",
              icon: "success",
              confirmButtonText: "OK",
              confirmButtonColor: "#2563eb",
              onConfirm: () => {
                window.close();
              },
              showCancelButton: false,
            });
          } catch (err) {
            showAlert({
              title: "Error",
              text: "No se pudo eliminar la grabación. La grabación continuará.",
              icon: "error",
              confirmButtonText: "OK",
              confirmButtonColor: "#2563eb",
              onConfirm: () => {},
              showCancelButton: false,
            });
          }
        },
        onCancel: () => {
          // No hacer nada, la grabación continúa
        },
      });
      return;
    }

    clearSyncInterval();
    clearAutoStopTimeout();
    setIsRecording(false);
    setIsSaving(true);

    try {
      await pushPositionsToBackend(id, positions);

      setRecordingFinished(true);
      showAlert({
        title: t("success"),
        text: t("trackingReplaySaved") || "Recorrido guardado correctamente",
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        onConfirm: () => {
          window.close();
        },
      });
    } catch (err) {
      showAlert({
        title: t("error"),
        text: getError(err, t),
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        onConfirm: () => {},
      });
      setIsRecording(true);
    } finally {
      setIsSaving(false);
    }
  }, [clearSyncInterval, clearAutoStopTimeout, pushPositionsToBackend, showAlert, t]);

  // Start recording when config is loaded
  useEffect(() => {
    if (!config || isRecording || replayIdRef.current) return;

    const startRecording = async () => {
      const userInfo = getUserInfo();
      if (!userInfo.id || !userInfo.company_id) {
        setError("No se pudo obtener el usuario o la compañía");
        return;
      }

      setIsSaving(true);
      const now = new Date();
      const nowIso = now.toISOString();
      const endDate = new Date(now.getTime() + config.durationHours * 60 * 60 * 1000);
      const endDateIso = endDate.toISOString();

      const firstPoint: TrackPoint = {
        lat: config.latitude,
        long: config.longitude,
        date: nowIso,
        event: {
          speed: config.speed ?? 0,
          ignition: config.engineStatus,
          battery: config.battery,
          gps_sat: config.gps_sat,
        },
      };
      const initialPositions = [firstPoint];

      try {
        const { data } = await api.post("/trackingReplays", {
          name: config.recordingName,
          created_by: userInfo.id,
          company_id: userInfo.company_id,
          vehicle_id: config.vehicleId,
          device_id: config.deviceId,
          start_date: nowIso,
          end_date: endDateIso,
          positions: formatPointsForApi(initialPositions),
        });

        const created = data?.data?.data;
        if (!created?.id) {
          throw new Error("Invalid create response");
        }

        addTrackingReplayToStore(created);
        replayIdRef.current = created.id;
        pointsRef.current = initialPositions;
        setTrackPoints(initialPositions);
        setIsRecording(true);
        setRecordingDuration(0);
        recordingStartTimeRef.current = Date.now();
        setRecordingEndDate(endDate);
        setRemainingSeconds(config.durationHours * 60 * 60);

        // Clear the config from sessionStorage
        const configKey = searchParams.get("configKey");
        if (configKey) {
          sessionStorage.removeItem(configKey);
        }
      } catch (err) {
        console.error("Error starting recording:", err);
        setError(getError(err, t));
      } finally {
        setIsSaving(false);
      }
    };

    void startRecording();
  }, [config, isRecording, getUserInfo, addTrackingReplayToStore, searchParams, t]);

  // Sync interval effect - save to backend every 5 minutes
  useEffect(() => {
    if (!isRecording || !replayIdRef.current) return;

    const tick = async () => {
      const id = replayIdRef.current;
      if (!id) return;

      const positions = pointsRef.current;
      if (positions.length === 0) return;

      try {
        await pushPositionsToBackend(id, positions);
      } catch (err) {
        console.error("Error syncing positions:", err);
      }
    };

    syncIntervalRef.current = setInterval(tick, SYNC_INTERVAL_MS);

    return () => {
      clearSyncInterval();
    };
  }, [isRecording, clearSyncInterval, pushPositionsToBackend]);

  // Timer effect using timestamps to work in background
  useEffect(() => {
    if (!isRecording || !recordingEndDate) return;

    // Store start time for accurate duration calculation
    if (!recordingStartTimeRef.current) {
      recordingStartTimeRef.current = Date.now();
    }

    let animationFrameId: number;
    let lastUpdate = Date.now();

    const updateTimer = () => {
      const now = Date.now();
      
      // Update every second (1000ms)
      if (now - lastUpdate >= 1000) {
        lastUpdate = now;
        
        // Calculate elapsed time based on timestamp (works in background)
        if (recordingStartTimeRef.current) {
          const elapsedSeconds = Math.floor((now - recordingStartTimeRef.current) / 1000);
          setRecordingDuration(elapsedSeconds);
        }
        
        // Calculate remaining time
        const remaining = Math.max(0, Math.floor((recordingEndDate.getTime() - now) / 1000));
        setRemainingSeconds(remaining);
      }
      
      animationFrameId = requestAnimationFrame(updateTimer);
    };

    // Start the update loop
    animationFrameId = requestAnimationFrame(updateTimer);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [isRecording, recordingEndDate]);

  // Handle visibility change - recalculate when tab becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!isRecording || !recordingEndDate || !recordingStartTimeRef.current) return;
      
      if (document.visibilityState === 'visible') {
        // Recalculate based on current timestamp when tab becomes visible
        const now = Date.now();
        const elapsedSeconds = Math.floor((now - recordingStartTimeRef.current) / 1000);
        setRecordingDuration(elapsedSeconds);
        
        const remaining = Math.max(0, Math.floor((recordingEndDate.getTime() - now) / 1000));
        setRemainingSeconds(remaining);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isRecording, recordingEndDate]);

  // Auto-stop effect
  useEffect(() => {
    if (!isRecording || !recordingEndDate) return;

    const now = new Date();
    const msUntilEnd = recordingEndDate.getTime() - now.getTime();

    if (msUntilEnd <= 0) {
      void stopRecording();
      return;
    }

    autoStopTimeoutRef.current = setTimeout(() => {
      void stopRecording();
    }, msUntilEnd);

    return () => {
      clearAutoStopTimeout();
    };
  }, [isRecording, recordingEndDate, stopRecording, clearAutoStopTimeout]);

  // Position capture interval - every 5 minutes (not real-time)
  useEffect(() => {
    if (!isRecording || !config?.deviceId) return;

    // Connect and subscribe to socket
    const socket = connectSocket();
    subscribeToDevices([config.deviceId]);

    // Listen for last_position responses
    const handleLastPosition = (position: any) => {
      // El deviceId puede venir en position.device_id o en point.device_id
      const responseDeviceId = position?.device_id ?? position?.point?.device_id;
      if (responseDeviceId !== config.deviceId) return;

      const point = position?.point;
      if (!point) return;

      const lat = point?.lat ?? point?.latitude;
      const lon = point?.lon ?? point?.longitude;
      const speed = point?.speed;
      const heading = point?.heading ?? point?.course ?? null;
      
      if (lat == null || lon == null) {
        return;
      }

      // Update current position and heading
      currentPositionRef.current = { lat, lng: lon };
      if (heading != null) {
        setCurrentHeading(heading);
      }

      // Add new track point
      const newPoint: TrackPoint = {
        lat,
        long: lon,
        date: new Date().toISOString(),
        heading: heading ?? undefined,
        event: {
          speed: speed ?? config?.speed ?? 0,
        },
      };

      pointsRef.current = [...pointsRef.current, newPoint];
      setTrackPoints(prev => [...prev, newPoint]);
    };

    socket.on("last_position", handleLastPosition);

    // Request position every 5 minutes
    const captureInterval = setInterval(() => {
      getLastPosition(config.deviceId, false, config.deviceId);
    }, SYNC_INTERVAL_MS);

    // Initial capture
    getLastPosition(config.deviceId, false, config.deviceId);

    return () => {
      socket.off("last_position", handleLastPosition);
      clearInterval(captureInterval);
    };
  }, [isRecording, config?.deviceId, config?.speed]);

  // Handle tab close - stop recording
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isRecording && replayIdRef.current) {
        // Try to save before closing
        const id = replayIdRef.current;
        const positions = pointsRef.current;
        
        // Use sendBeacon for reliable data sending on page unload
        const payload = JSON.stringify({
          end_date: new Date().toISOString(),
          positions: formatPointsForApi(positions),
        });
        
        navigator.sendBeacon(
          `/api/trackingReplays?id=${id}`,
          new Blob([payload], { type: "application/json" })
        );

        e.preventDefault();
        e.returnValue = t("trackingReplayCloseWarning") || "La grabación se detendrá si cierras esta pestaña.";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isRecording, t]);

  // Map configuration
  const mapContainerStyle = {
    width: "100%",
    height: "100%",
  };

  const center = currentPositionRef.current || DEFAULT_CENTER;

  // Vehicle status from config or default to stopped
  const vehicleStatus = useMemo(() => config?.status || "stopped", [config?.status]);
  const vehicleHeading = useMemo(() => currentHeading ?? config?.heading ?? 0, [currentHeading, config?.heading]);
  const markerColor = useMemo(() => getStatusColor(vehicleStatus), [vehicleStatus]);
  const markerStyle = useMemo(() => getMarkerShape(vehicleStatus, vehicleHeading), [vehicleStatus, vehicleHeading]);

  // Get marker position
  const markerPosition = currentPositionRef.current;

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${themeClasses.bg}`}>
        <div className={`p-6 rounded-lg max-w-md text-center ${themeClasses.cardBg} border ${themeClasses.border} shadow-lg`}>
          <i className="ri-error-warning-line text-4xl text-red-500 mb-4"></i>
          <h2 className={`text-xl font-semibold mb-2 ${themeClasses.text}`}>
            {t("error")}
          </h2>
          <p className={`mb-4 ${themeClasses.textSecondary}`}>{error}</p>
          <button
            type="button"
            onClick={() => window.close()}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg"
          >
            {t("close") || "Cerrar"}
          </button>
        </div>
      </div>
    );
  }

  if (recordingFinished) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${themeClasses.bg}`}>
        <div className={`p-6 rounded-lg max-w-md text-center ${themeClasses.cardBg} border ${themeClasses.border} shadow-lg`}>
          <i className="ri-checkbox-circle-line text-4xl text-green-500 mb-4"></i>
          <h2 className={`text-xl font-semibold mb-2 ${themeClasses.text}`}>
            {t("trackingReplaySaved") || "Grabación guardada"}
          </h2>
          <p className={`mb-4 ${themeClasses.textSecondary}`}>
            {t("trackingReplayCanClose") || "Puedes cerrar esta pestaña."}
          </p>
          <button
            type="button"
            onClick={() => window.close()}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg"
          >
            {t("close") || "Cerrar"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-full flex">
      {/* Sidebar - Collapsed version */}
      {sidebarCollapsed ? (
        <div
          className={`w-16 ${themeClasses.bg} border-r ${themeClasses.border} flex flex-col h-full transition-all duration-300 flex-shrink-0`}
        >
          <div
            className={`p-3 border-b ${themeClasses.border} flex flex-col items-center space-y-2`}
          >
            <button
              onClick={() => setSidebarCollapsed(false)}
              className={`p-2 ${themeClasses.buttonHover} rounded-lg transition-colors`}
              title="Expandir sidebar"
            >
              <i className="ri-menu-unfold-line text-primary-500 text-lg"></i>
            </button>

            {/* Recording indicator - compact */}
            <div className="flex flex-col items-center" title="Grabando">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className={`text-xs font-bold ${themeClasses.text} mt-1`}>
                {trackPoints.length}
              </span>
            </div>

            {/* Elapsed time */}
            <div className="flex flex-col items-center" title="Tiempo transcurrido">
              <div className="w-3 h-3 bg-primary-400 rounded-full"></div>
              <span className={`text-xs font-bold ${themeClasses.text} mt-1`}>
                {Math.floor(recordingDuration / 60)}
              </span>
            </div>

            {/* Remaining time */}
            <div className="flex flex-col items-center" title="Tiempo restante">
              <div className="w-3 h-3 bg-amber-400 rounded-full"></div>
              <span className={`text-xs font-bold ${themeClasses.text} mt-1`}>
                {Math.floor(remainingSeconds / 60)}
              </span>
            </div>
          </div>

          {/* Collapsed vehicle info */}
          <div className="flex-1 flex flex-col items-center justify-center">
            <div className={`text-2xl font-bold ${themeClasses.text} mb-1`}>
              {trackPoints.length}
            </div>
            <div className={`text-xs ${themeClasses.textSecondary} text-center px-1`}>
              Puntos
            </div>
          </div>

          {/* Stop button - compact */}
          <div className="p-2 border-t border-dark-border">
            <button
              type="button"
              onClick={() => void stopRecording()}
              disabled={isSaving || !isRecording}
              className="w-full p-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center"
              title="Detener y guardar"
            >
              <i className="ri-stop-circle-fill"></i>
            </button>
          </div>
        </div>
      ) : (
        /* Sidebar - Expanded version */
        <aside className={`w-80 flex-shrink-0 flex flex-col h-full ${themeClasses.bg} border-r ${themeClasses.border} transition-all duration-300`}>
          {/* Sidebar Header */}
          <div className={`p-4 border-b ${themeClasses.border}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                  <i className="ri-record-circle-line text-white"></i>
                </div>
                <div>
                  <h1 className={`text-sm font-semibold ${themeClasses.text}`}>
                    {config?.recordingName || t("trackingReplayRecording") || "Grabando"}
                  </h1>
                  <p className={`text-xs ${themeClasses.textSecondary}`}>
                    {t("recordingInProgress") || "Grabación en curso"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSidebarCollapsed(true)}
                className={`p-2 ${themeClasses.buttonHover} rounded-lg transition-colors`}
                title="Contraer sidebar"
              >
                <i className="ri-menu-fold-line text-primary-500 text-sm"></i>
              </button>
            </div>
          </div>

          {/* Recording Stats */}
          <div className="p-4 grid grid-cols-2 gap-2">
            <div className={`p-3 rounded-lg ${themeClasses.cardBg} border ${themeClasses.cardBorder}`}>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <span className={`text-xs ${themeClasses.textSecondary}`}>
                  {t("vehicleCard.elapsedTime") || "Transcurrido"}
                </span>
              </div>
              <span className={`text-lg font-mono font-medium ${themeClasses.text}`}>
                {formatDuration(recordingDuration)}
              </span>
            </div>
            <div className={`p-3 rounded-lg ${themeClasses.cardBg} border border-amber-500/30`}>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                <span className={`text-xs text-amber-400`}>
                  {t("vehicleCard.remainingTime") || "Restante"}
                </span>
              </div>
              <span className={`text-lg font-mono font-medium text-amber-400`}>
                {formatDuration(remainingSeconds)}
              </span>
            </div>
          </div>

          {/* Vehicle Info Card */}
          <div className="px-4 pb-4">
            <div className={`p-3 rounded-lg ${themeClasses.cardBg} border ${themeClasses.cardBorder} border-l-4 border-l-red-500`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <i className="ri-truck-line text-primary-500"></i>
                  <span className={`text-sm font-medium ${themeClasses.text}`}>
                    {config?.vehicleName}
                  </span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full bg-red-500 text-white`}>
                  {t("recording") || "Grabando"}
                </span>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-xs">
                  <i className="ri-speed-line text-gray-400"></i>
                  <span className={themeClasses.textSecondary}>
                    {trackPoints.length} {t("pointsRecorded") || "puntos grabados"}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <i className="ri-time-line text-gray-400"></i>
                  <span className={themeClasses.textSecondary}>
                    {t("syncEvery") || "Sinc. cada"} 5 min
                  </span>
                </div>
                {currentPositionRef.current && (
                  <div className="flex items-center gap-2 text-xs">
                    <i className="ri-map-pin-line text-gray-400"></i>
                    <span className={themeClasses.textSecondary}>
                      {currentPositionRef.current.lat.toFixed(4)}, {currentPositionRef.current.lng.toFixed(4)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Button */}
          <div className="px-4 mt-auto pb-4">
            <button
              type="button"
              onClick={() => void stopRecording()}
              disabled={isSaving || !isRecording}
              className="w-full px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <i className="ri-stop-circle-fill"></i>
              {isSaving ? (t("saving") || "Guardando...") : (t("vehicleCard.stopAndSave") || "Detener y guardar")}
            </button>
            <p className={`mt-2 text-xs text-center ${themeClasses.textSecondary}`}>
              {t("trackingReplayTabInfo") || "No cierres esta pestaña mientras grabas"}
            </p>
          </div>

          {/* Footer */}
          <div className={`p-3 border-t ${themeClasses.border} ${themeClasses.cardBg}`}>
            <div className="text-center">
              <p className={`text-xs ${themeClasses.text} font-medium`}>
                {trackPoints.length} {t("pointsRecorded") || "puntos grabados"}
              </p>
              <p className={`text-xs ${themeClasses.textSecondary}`}>
                {config?.durationHours} {config?.durationHours === 1 ? "hora" : "horas"} {t("vehicleCard.duration") || "duración"}
              </p>
            </div>
          </div>
        </aside>
      )}

      {/* Map Container - Right side */}
      <div className="flex-1 relative">
        {/* Real-time indicator */}
        <div className="absolute top-4 right-4 z-10 bg-white rounded-lg shadow-lg px-3 py-2 flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${isRecording ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
          <span className="text-xs font-medium text-gray-700">
            {isRecording ? (t("recording") || 'Grabando') : (t("ready") || 'Listo')}
          </span>
        </div>

        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={center}
            zoom={DEFAULT_ZOOM}
            options={{
              minZoom: 3,
              maxZoom: 20,
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: false,
              zoomControl: true,
              zoomControlOptions: {
                position: google.maps.ControlPosition.RIGHT_CENTER,
              },
            }}
          >
            {markerPosition && (
              <OverlayView
                position={markerPosition}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                getPixelPositionOffset={() => ({
                  x: -(markerStyle.size / 2),
                  y: -(markerStyle.size + 20),
                })}
              >
                <div
                  style={{
                    width: `${markerStyle.size}px`,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <div
                    className="bg-gray-900 text-white text-[10px] px-1.5 py-0.5 rounded shadow-lg whitespace-nowrap"
                    style={{ marginBottom: "2px" }}
                  >
                    {config?.vehicleName || "Vehículo"}
                  </div>
                  {markerStyle.hasBorder && markerStyle.clipPath ? (
                    // Driving: arrow shape with border simulation and rotation
                    <div
                      style={{
                        position: "relative",
                        width: `${markerStyle.size}px`,
                        height: `${markerStyle.size}px`,
                        filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))",
                        transform: `rotate(${markerStyle.rotation}deg)`,
                        transformOrigin: "center center",
                      }}
                    >
                      {/* Outer layer (border) */}
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          backgroundColor: "#ffffff",
                          clipPath: markerStyle.clipPath,
                        }}
                      />
                      {/* Inner layer (color) */}
                      <div
                        style={{
                          position: "absolute",
                          top: `${markerStyle.borderWidth}px`,
                          left: `${markerStyle.borderWidth}px`,
                          width: `calc(100% - ${markerStyle.borderWidth * 2}px)`,
                          height: `calc(100% - ${markerStyle.borderWidth * 2}px)`,
                          backgroundColor: markerColor,
                          clipPath: markerStyle.clipPath,
                        }}
                      />
                    </div>
                  ) : (
                    // Other states: square or circle with icon
                    <div
                      style={{
                        width: `${markerStyle.size}px`,
                        height: `${markerStyle.size}px`,
                        backgroundColor: markerColor,
                        borderRadius: markerStyle.borderRadius,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "3px solid #ffffff",
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
                      }}
                    >
                      {markerStyle.icon && (
                        <i className={`${markerStyle.icon} text-white text-sm`}></i>
                      )}
                    </div>
                  )}
                </div>
              </OverlayView>
            )}
            {/* Numbered markers for each track point */}
            {trackPoints.map((point, index) => (
              <OverlayView
                key={`point-${index}-${point.date}`}
                position={{ lat: point.lat, lng: point.long }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                getPixelPositionOffset={() => ({
                  x: -15,
                  y: -15,
                })}
              >
                <div
                  style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    backgroundColor: '#3B82F6',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    border: '3px solid white',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                    cursor: 'pointer',
                  }}
                  title={`Punto ${index + 1} - ${new Date(point.date).toLocaleTimeString()}`}
                >
                  {index + 1}
                </div>
              </OverlayView>
            ))}
          </GoogleMap>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        )}
      </div>
    </div>
  );
}
